import { useQuery } from "@tanstack/react-query";
import { axios } from "@/client/libs/axios";

// Types for feature settings
export interface CoverLetterSettings {
  enabled: boolean;
  templatesAllowed: string[];
  tokenCost: number;
  maxPerDay: number;
  maxContentLength: number;
}

export interface JobTrackerSettings {
  enabled: boolean;
  maxApplicationsPerUser: number;
  atsAnalysisCost: number;
  urlImportCost: number;
  tailorResumeCost: number;
  coverLetterCost: number;
  maxApplicationsPerPlan: {
    FREE: number;
    BASIC: number;
    PROFESSIONAL: number;
    ENTERPRISE: number;
  };
}

export type StripeUiType = "card" | "payment_element";

export interface StripeCheckoutSettings {
  stripeUi: StripeUiType;
}

export interface FeatureSettingsResponse {
  coverLetter: {
    enabled: boolean;
    settings: CoverLetterSettings;
  };
  jobTracker: {
    enabled: boolean;
    settings: JobTrackerSettings;
  };
  stripeCheckout?: {
    enabled: boolean;
    settings: StripeCheckoutSettings;
  };
}

// Default settings (fallback if API fails)
const DEFAULT_COVER_LETTER_SETTINGS: CoverLetterSettings = {
  enabled: true,
  templatesAllowed: ["clean", "classic", "executive", "executive-neat", "modern", "modern-corporate"],
  tokenCost: 50,
  maxPerDay: 10,
  maxContentLength: 5000,
};

const DEFAULT_JOB_TRACKER_SETTINGS: JobTrackerSettings = {
  enabled: true,
  maxApplicationsPerUser: 100,
  atsAnalysisCost: 30,
  urlImportCost: 20,
  tailorResumeCost: 40,
  coverLetterCost: 50,
  maxApplicationsPerPlan: {
    FREE: 0,
    BASIC: 0,
    PROFESSIONAL: 100,
    ENTERPRISE: 500,
  },
};

const fetchFeatureSettings = async (): Promise<FeatureSettingsResponse> => {
  try {
    // Use public config endpoint (no auth required)
    const response = await axios.get<FeatureSettingsResponse>("/config/features/status");
    return response.data;
  } catch (error) {
    console.warn("Failed to fetch feature settings, using defaults", error);
    // Return defaults if API fails
    return {
      coverLetter: {
        enabled: true,
        settings: DEFAULT_COVER_LETTER_SETTINGS,
      },
      jobTracker: {
        enabled: true,
        settings: DEFAULT_JOB_TRACKER_SETTINGS,
      },
      stripeCheckout: {
        enabled: true,
        settings: { stripeUi: "card" as StripeUiType },
      },
    };
  }
};

/**
 * Hook to access feature settings from admin panel
 * Caches settings for 5 minutes to avoid excessive API calls
 */
export const useFeatureSettings = () => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["feature-settings"],
    queryFn: fetchFeatureSettings,
    staleTime: 60 * 1000, // 1 minute so admin Stripe UI changes propagate sooner
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 1,
    refetchOnWindowFocus: true, // pick up admin changes when user switches back to payment tab
  });

  // Cover Letter helpers
  const coverLetter = {
    enabled: data?.coverLetter?.enabled ?? true,
    settings: {
      ...DEFAULT_COVER_LETTER_SETTINGS,
      ...data?.coverLetter?.settings,
    },
    isTemplateAllowed: (templateId: string) => {
      const allowed = data?.coverLetter?.settings?.templatesAllowed ?? DEFAULT_COVER_LETTER_SETTINGS.templatesAllowed;
      return allowed.includes(templateId);
    },
  };

  // Job Tracker helpers
  const jobTracker = {
    enabled: data?.jobTracker?.enabled ?? true,
    settings: {
      ...DEFAULT_JOB_TRACKER_SETTINGS,
      ...data?.jobTracker?.settings,
    },
    getMaxApplications: (tier: string) => {
      const limits = data?.jobTracker?.settings?.maxApplicationsPerPlan ?? DEFAULT_JOB_TRACKER_SETTINGS.maxApplicationsPerPlan;
      return limits[tier as keyof typeof limits] ?? limits.FREE;
    },
  };

  // Stripe Checkout: which form to show (card fields vs Payment Element)
  const stripeCheckout = {
    enabled: data?.stripeCheckout?.enabled ?? true,
    stripeUi: (data?.stripeCheckout?.settings?.stripeUi ?? "card") as StripeUiType,
  };

  return {
    isLoading,
    error,
    refetch,
    isFetching,
    coverLetter,
    jobTracker,
    stripeCheckout,
    // Raw data access
    raw: data,
  };
};

/**
 * Hook specifically for cover letter feature
 */
export const useCoverLetterFeature = () => {
  const { coverLetter, isLoading } = useFeatureSettings();
  return { ...coverLetter, isLoading };
};

/**
 * Hook specifically for job tracker feature  
 */
export const useJobTrackerFeature = () => {
  const { jobTracker, isLoading } = useFeatureSettings();
  return { ...jobTracker, isLoading };
};
